import React, { useState, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useDispatch } from 'react-redux';
import { Button, Form, Icon } from '@gumtree/ui-library';
import FormElement from '@gumtree/ui-library/src/form/form-element';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import { sanitizeString } from '@gumtree/ui-library/src/utils/string-service';
import { isSameLocationName } from '@gumtree/shared/src/util/location-selection-persist';
import { trackGA4PreNav } from '@gumtree/shared/src/util/ga4-shared';

import { Device } from '@gumtree/shared/src/types/client-data';
import Input from '@gumtree/ui-library/src/form/input';
import { getDistanceFromUserPref } from '@gumtree/shared/src/util/user-pref-cookie-service';

import KeywordSearchField from './keyword-search-field/keyword-search-field-container';
import LocationField from './location-field/location-field-container';
import { setupSuggestionsService, submitSearch, setIsSubmitSearchCalled } from './search-bar-service';
import './opensearch.xml';
import { toggleSearchModal } from './search-bar-actions';
import { SearchModal } from './mobile-modal/modal';
import { OnlyShowOnLargeScreen, OnlyShowOnSmallScreen } from '../utils/screens.style';
import { searchFieldPlaceholderText } from './constants';
import type { SearchBarKeywordSelection, SearchBarKeywordState } from './reducers/keyword-reducer';
import type {
    SearchBarLocationSelection,
    SearchBarLocationState,
} from './reducers/location-reducer';
import { searchBarContainerCss } from './search-bar.style';
import { TrackingUtil } from './tracking-util';
import type { G5SParams } from './reducers/g5s-params';
import type { SearchSubmitSource } from './search-bar-service';

interface SearchBarProps {
    device: Device | undefined;
    popularSearches: {
        showPopularSearches: boolean;
        queryKeywords?: Array<{ name: string; url: string }>;
    };
    keyword?: SearchBarKeywordState;
    selectedKeyword?: SearchBarKeywordSelection;
    selectedLocation?: SearchBarLocationSelection;
    location?: SearchBarLocationState;
    showSearchModal: boolean;
    showSearchBarOnMobile: boolean;
    outsideFocus?: boolean;
    g5SParams: G5SParams;
    cookieDomain?: string;
    focusSearchInputOnOpenSynchronously?: boolean;
    isSafariMobileWeb?: boolean;
}

export const submitSearchHandler = async ({
    setIsSearchSubmitted,
    currentKeyword,
    selectedKeyword,
    selectedLocation,
    currentLocationDetails,
    currentLocation,
    requireKeywordCorrection = false,
    keywordPopulatedBy,
    locationPopulatedBy,
    keywordSuggestions,
    keywordSuggestionsLatency,
    keywordSuggestionsLatencyGroup,
    cookieDomain,
    submitSource,
    pageLocation,
}: {
    setIsSearchSubmitted: (value: boolean) => void;
    currentKeyword: string | undefined;
    selectedKeyword: SearchBarKeywordSelection | undefined;
    selectedLocation: SearchBarLocationSelection | undefined;
    currentLocationDetails: SearchBarLocationSelection | undefined;
    currentLocation: string | undefined;
    requireKeywordCorrection: boolean;
    keywordPopulatedBy: GA4.SubmitListingSearchEvent['search']['keywordPopulatedBy'];
    locationPopulatedBy: GA4.SubmitListingSearchEvent['search']['locationPopulatedBy'];
    keywordSuggestions: string[];
    keywordSuggestionsLatency?: number | undefined;
    keywordSuggestionsLatencyGroup?: string | undefined;
    cookieDomain?: string;
    submitSource: SearchSubmitSource;
    pageLocation?: string;
}) => {
    setIsSearchSubmitted(true);
    const submittableKeyword = selectedKeyword?.name ? selectedKeyword?.name : currentKeyword;
    const submittableLocation = selectedLocation?.name ? selectedLocation?.name : currentLocation;

    const sanitizedKeyword = submittableKeyword
        ? sanitizeString(submittableKeyword)
        : submittableKeyword;
    const sanitizedLocation = submittableLocation
        ? sanitizeString(submittableLocation)
        : submittableLocation;

    trackGA4PreNav<GA4.SubmitListingSearchEvent>({
        event: 'submit_listing_search',
        search: {
            locationPopulatedBy,
            locationInput: currentLocation,
            locationSelected: selectedLocation?.name || currentLocation,
            keywordPopulatedBy,
            keywordInput: currentKeyword,
            keywordSelected: selectedKeyword?.name,
            keywordSuggestions: keywordSuggestions.join(','),
            keywordSuggestionsVolume: keywordSuggestions?.length,
            keywordInputCharCount: currentKeyword?.length,
            keywordSuggestionsLatency,
            keywordSuggestionsLatencyGroup,
        },
    });

    const distance = getDistanceFromUserPref();

    submitSearch({
        category: selectedKeyword?.category,
        categoryDisplayName: selectedKeyword?.categoryDisplayName,
        categoryId: selectedKeyword?.categoryId,
        keyword: sanitizedKeyword,
        location: sanitizedLocation,
        locationDetails: selectedLocation,
        currentLocationDetails,
        requireKeywordCorrection,
        searchOptionsExactMatch: Boolean(selectedKeyword?.searchOptionsExactMatch),
        additionalParams: {
            ...(distance ? { distance } : {}),
        },
        keywordPopulatedBy,
        cookieDomain,
        submitSource,
        pageLocation,
    });
};

const SearchBar: React.FC<SearchBarProps> = ({
    showSearchModal,
    keyword,
    location,
    popularSearches,
    showSearchBarOnMobile,
    outsideFocus,
    g5SParams,
    cookieDomain,
    focusSearchInputOnOpenSynchronously = false,
    isSafariMobileWeb = false,
}) => {
    const dispatch = useDispatch();

    const [isSearchSubmitted, setIsSearchSubmitted] = useState(false);
    const [focusInputOnOpenFromTouch, setFocusInputOnOpenFromTouch] = useState(false);
    const isOpeningRef = useRef(false);
    const rafIdRef = useRef<number | null>(null);

    const handleToggleSearchDialog = useCallback(() => {
        if (isOpeningRef.current) {
            return;
        }

        isOpeningRef.current = true;
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current);
        }

        rafIdRef.current = requestAnimationFrame(() => {
            setTimeout(() => {
                dispatch(toggleSearchModal());
                isOpeningRef.current = false;
                rafIdRef.current = null;
            }, 0);
        });
    }, [dispatch]);

    const handleMobileFakeInputTouchStart = useCallback(
        (event: React.TouchEvent<HTMLInputElement>) => {
            event.preventDefault();
            if (isOpeningRef.current) {
                return;
            }

            isOpeningRef.current = true;
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
                rafIdRef.current = null;
            }

            flushSync(() => {
                setFocusInputOnOpenFromTouch(true);
                dispatch(toggleSearchModal());
            });

            window.setTimeout(() => {
                isOpeningRef.current = false;
            }, 0);
        },
        [dispatch]
    );

    useEffect(() => {
        if (!showSearchModal) {
            setFocusInputOnOpenFromTouch(false);
        }
    }, [showSearchModal]);

    useEffect(() => {
        setupSuggestionsService(g5SParams);
    }, []);

    useEffect(() => {
        const resetSubmitGuard = () => {
            setIsSubmitSearchCalled(false);
        };
        const handlePageShow = (event: PageTransitionEvent) => {
            if (event.persisted) {
                resetSubmitGuard();
            }
        };
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                resetSubmitGuard();
            }
        };

        // Reset once on mount and when returning to this page context (e.g. BFCache restore or app switch back).
        resetSubmitGuard();
        window.addEventListener('pageshow', handlePageShow);
        window.addEventListener('focus', resetSubmitGuard);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('pageshow', handlePageShow);
            window.removeEventListener('focus', resetSubmitGuard);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current);
            }
        };
    }, []);

    useEffect(() => {
        // upon selecting a suggestion
        if (keyword?.selectedSuggestion || location?.selectedSuggestion) {
            const keywordPopulatedBy = TrackingUtil.getPopulatedBy(keyword);
            const keywordSuggestionsLatency = TrackingUtil.getKeywordSuggestionsLatency(
                keywordPopulatedBy,
                keyword
            );

            submitSearchHandler({
                setIsSearchSubmitted,
                currentKeyword: keyword?.value,
                selectedKeyword: keyword?.selectedSuggestion,
                selectedLocation: location?.selectedSuggestion,
                currentLocationDetails: location?.currentLocationDetails,
                currentLocation: location?.value,
                keywordPopulatedBy,
                locationPopulatedBy: TrackingUtil.getPopulatedBy(location),
                keywordSuggestions: TrackingUtil.getKeywordSuggestions(keyword),
                requireKeywordCorrection: false,
                keywordSuggestionsLatency,
                keywordSuggestionsLatencyGroup: keywordSuggestionsLatency
                    ? TrackingUtil.getLatencyCategory(keywordSuggestionsLatency)
                    : undefined,
                cookieDomain,
                submitSource: 'suggestion',
            });
        }
    }, [keyword?.selectedSuggestion, location?.selectedSuggestion, cookieDomain]);

    return (
        <div className="search-bar-container" css={searchBarContainerCss}>
            <Form
                action="/search"
                onSubmit={(e) => {
                    const submitter = (e as any)?.nativeEvent?.submitter as
                        | (HTMLButtonElement & { dataset?: DOMStringMap })
                        | undefined;

                    if (submitter?.dataset?.q === 'dialog-close') {
                        e.preventDefault();
                        return;
                    }

                    const form = e.currentTarget;
                    // Get current values directly from DOM to avoid stale state
                    // This ensures we get the latest values even if debounce hasn't updated Redux state
                    const keywordInput = form.querySelector('input[name="q"]') as HTMLInputElement;
                    const locationInput = (form.querySelector('input[name="search_location"]') ||
                        form.querySelector('input[name="header-search-location"]') ||
                        form.querySelector('input#header-search-location')) as HTMLInputElement;
                    const currentKeywordFromDOM = keywordInput?.value || keyword?.value || '';
                    const currentLocationFromDOM = locationInput?.value || location?.value || '';

                    const candidate =
                        location?.selectedSuggestion ??
                        (location?.suggestions?.length && !location?.useRecentHistory
                            ? (location.suggestions[0] as SearchBarLocationSelection)
                            : undefined);
                    const effectiveLocation = isSameLocationName(
                        location?.value,
                        currentLocationFromDOM
                    )
                        ? candidate
                        : undefined;

                    submitSearchHandler({
                        setIsSearchSubmitted,
                        currentKeyword: currentKeywordFromDOM,
                        selectedKeyword: keyword?.selectedSuggestion,
                        selectedLocation: effectiveLocation,
                        currentLocationDetails: location?.currentLocationDetails,
                        currentLocation: currentLocationFromDOM,
                        keywordPopulatedBy: TrackingUtil.getPopulatedBy(keyword, true),
                        locationPopulatedBy: TrackingUtil.getPopulatedBy(location, true),
                        keywordSuggestions: TrackingUtil.getKeywordSuggestions(keyword),
                        requireKeywordCorrection: true,
                        cookieDomain,
                        submitSource: 'form',
                        pageLocation: location?.initialValue,
                    });
                }}
                data-testid="submit-form"
            >
                <div className="search-bar" {...qaAttribute('search-bar')}>
                    <SearchModal
                        isOpen={showSearchModal}
                        onClose={handleToggleSearchDialog}
                        focusInputOnOpenSynchronously={
                            focusSearchInputOnOpenSynchronously || focusInputOnOpenFromTouch
                        }
                    />

                    {showSearchBarOnMobile && (
                        <OnlyShowOnSmallScreen>
                            <div className="inputs-container--fake-search">
                                <FormElement type="input" className="input-field">
                                    <div className="icon-container">
                                        <Icon type="magnifying-glass" size="medium" />
                                    </div>
                                    <Input
                                        placeholder={searchFieldPlaceholderText}
                                        autoComplete="off"
                                        className="input"
                                        id="search-bar-input"
                                        name="search-bar-input"
                                        readOnly={isSafariMobileWeb}
                                        onTouchStart={
                                            isSafariMobileWeb
                                                ? handleMobileFakeInputTouchStart
                                                : undefined
                                        }
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleToggleSearchDialog();
                                        }}
                                        onFocus={handleToggleSearchDialog}
                                        value={keyword?.value || ''}
                                    />
                                </FormElement>
                            </div>
                        </OnlyShowOnSmallScreen>
                    )}

                    <OnlyShowOnLargeScreen>
                        <div className="inputs-container--real-search">
                            <div className="keyword-search-container">
                                <KeywordSearchField outsideFocus={outsideFocus} />
                            </div>
                            <div className="dividing-line" />
                            <div className="location-container">
                                <LocationField />
                                <Button
                                    className="search-button"
                                    display="primary"
                                    isLoading={isSearchSubmitted}
                                    loadingText=""
                                    label={<Icon type="magnifying-glass" />}
                                    type="submit"
                                    {...qaAttribute('search-button')}
                                />
                            </div>
                        </div>
                    </OnlyShowOnLargeScreen>

                    {popularSearches.showPopularSearches && popularSearches.queryKeywords && (
                        <div className="popular-search-wrapper">
                            <input type="checkbox" id="popular-search-container-input" hidden />
                            <ul className="popular-search-container">
                                <li className="popular-search-container-title">Top searches: </li>
                                {popularSearches.queryKeywords.map((el) => (
                                    <li key={el.name}>
                                        <a href={el.url}>{el.name}</a>
                                    </li>
                                ))}
                            </ul>
                            {}
                            <label
                                htmlFor="popular-search-container-input"
                                className="expand-label"
                            >
                                <Icon type="dropdown" />
                            </label>
                        </div>
                    )}
                </div>
            </Form>
        </div>
    );
};

export default SearchBar;
